<script setup lang="ts">
import {computed, ref} from 'vue'
import {Check, ChevronsUpDown} from 'lucide-vue-next'
import {Button} from '@/components/ui/button'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command'
import {cn} from '@/lib/utils'
import type {OpenRouterModel} from '@shared/llm_types'
import {formatModelPrice} from '@/lib/model-pricing'

const props = defineProps<{model: string | null; options: OpenRouterModel[]; missing?: boolean}>()
const emit = defineEmits<{select: [model: string | null]}>()
const open = ref(false)
const selected = computed(() => props.options.find((item) => item.id === props.model))

function select(model: string): void {
  emit('select', model)
  open.value = false
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button variant="outline" role="combobox" :aria-expanded="open" class="w-full justify-between text-left font-normal" :class="missing && 'border-destructive'">
        <span class="truncate">{{ selected?.name || selected?.id || model || 'Select a model' }}</span>
        <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[min(34rem,calc(100vw-2rem))] p-0">
      <Command>
        <CommandInput placeholder="Search model..." />
        <CommandEmpty>No compatible models found.</CommandEmpty>
        <CommandList>
          <p v-if="!options.length" class="py-6 text-center text-sm text-muted-foreground">No compatible models found.</p>
          <CommandGroup>
            <CommandItem v-for="item in options" :key="item.id" :value="item.id" @select="select(item.id)">
              <Check :class="cn('mr-2 size-4', model === item.id ? 'opacity-100' : 'opacity-0')" />
              <span class="min-w-0 flex-1 truncate">{{ item.name }} ({{ item.id }})</span>
              <span class="ml-2 shrink-0 text-xs text-muted-foreground">{{ formatModelPrice(item.pricing.prompt) }} | {{ formatModelPrice(item.pricing.completion) }}</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
